import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { EditProjectService } from './edit-project.modal.service';
import { OperatorsService } from '../../operators/operators.service';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.modal.component.html',
  styleUrls: ['./edit-project.modal.component.scss'],
})
export class EditProjectModalComponent implements OnInit {
  @ViewChild('requestPasswordInsert') requestPasswordInsert!: TemplateRef<any>;
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';

  private _project: any;
  private passModalRef?: NgbModalRef;

  readonly separatorKeys = [ENTER, COMMA] as const;

  @Input() set project(value: any) {
    this._project = value;
    if (value) this.patchInitialValues();
  }

  get project(): any {
    return this._project;
  }

  public isProjectExpanded = true;
  public isCompanyExpanded = false;
  public isAddressExpanded = false;
  public isSponsorExpanded = false;
  public isFinancialExpanded = false;
  public isPermissionsExpanded = false;
  public isListCnpjExpanded = false;
  public isCertificateExpanded = false;
  public isIpExpanded = false;

  public isSaving = false;
  public validatingPassword = false;
  public errorMessage = '';
  public passwordInsert = '';

  public licensingsLoading = false;
  public licensingMap: { [id: string]: string } = {};
  public licensingId = '';
  public licensingFallbackLabel = '-';
  public accountGuid: any = '';

  public platformsLoading = false;
  public platformOptions: any[] = [];



  projectForm = new FormGroup(
    {
      id: new FormControl<string>('', { nonNullable: true }),

      operatorRegistration: new FormGroup({
        id: new FormControl<string>('', { nonNullable: true }),
        nameProject: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        site: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        productId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        bankProvider: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        currentPlatform: new FormControl<string>('', { nonNullable: true }),
        percentGGR: new FormControl<number>(0, { nonNullable: true }),
      }),

      company: new FormGroup({
        cnpj: new FormControl<string>('', { nonNullable: true }),
        razaoSocial: new FormControl<string>('', { nonNullable: true }),
        email: new FormControl<string>('', { nonNullable: true }),
        phone: new FormControl<string>('', { nonNullable: true }),
      }),

      adress: new FormGroup({
        logradouro: new FormControl<string>('', { nonNullable: true }),
        numero: new FormControl<string>('', { nonNullable: true }),
        complemento: new FormControl<string>('', { nonNullable: true }),
        cep: new FormControl<string>('', { nonNullable: true }),
        bairro: new FormControl<string>('', { nonNullable: true }),
        cidade: new FormControl<string>('', { nonNullable: true }),
        estado: new FormControl<string>('', { nonNullable: true }),
      }),

      sponsor: new FormGroup({
        cpf: new FormControl<string>('', { nonNullable: true }),
        name: new FormControl<string>('', { nonNullable: true }),
        email: new FormControl<string>('', { nonNullable: true }),
        phone: new FormControl<string>('', { nonNullable: true }),
      }),

      depositValueMinRequest: new FormControl<number>(0, { nonNullable: true }),
      depositValueMaxRequest: new FormControl<number>(0, { nonNullable: true }),
      valorMinimoSaqueAutomatico: new FormControl<number>(0, { nonNullable: true }),
      amountExpired: new FormControl<number>(0, { nonNullable: true }),
      valueExpired: new FormControl<number>(0, { nonNullable: true }),
      analyzeWithdrawal: new FormControl<boolean>(false, { nonNullable: true }),
      valueMinRequest: new FormControl<number>(0, { nonNullable: true }),
      valueMaxRequest: new FormControl<number>(0, { nonNullable: true }),

      permissonWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
      permissonDeposit: new FormControl<boolean>(false, { nonNullable: true }),
      permissonTef: new FormControl<boolean>(false, { nonNullable: true }),
      permissonCrypto: new FormControl<boolean>(false, { nonNullable: true }),
      permissionGameApi: new FormControl<boolean>(false, { nonNullable: true }),

      featureFlagRefundPix: new FormControl<boolean>(false, { nonNullable: true }),
      featureFlagCpfWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
      featureFlagCustomerBalance: new FormControl<boolean>(false, { nonNullable: true }),
      featureFlagNewParam: new FormControl<boolean>(false, { nonNullable: true }),

      userBlackList: new FormControl<boolean>(false, { nonNullable: true }),

      listCnpj: new FormGroup({
        flagCnpjCashIn: new FormControl<boolean>(false, { nonNullable: true }),
        flagCnpjCashOut: new FormControl<boolean>(false, { nonNullable: true }),
        cnpjCashIn: new FormControl<string[]>([]),
        cnpjCashOut: new FormControl<string[]>([]),
      }),

      validateIp: new FormControl<boolean>(false, { nonNullable: true }),
      listsIp: new FormControl<string[]>([]),

      mtls: new FormGroup({
        deposit: new FormControl<boolean>(false, { nonNullable: true }),
        withdraw: new FormControl<boolean>(false, { nonNullable: true }),
        tef: new FormControl<boolean>(false, { nonNullable: true }),
      }),
    },
    { validators: [this.rangesValidator] }
  );

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private editProjectService: EditProjectService,
    private operatorsService: OperatorsService
  ) { }

  ngOnInit(): void {
    this.patchInitialValues();
    this.loadLicensings();
    this.loadPlatforms();

    this.projectForm.controls.operatorRegistration.controls.currentPlatform.valueChanges.subscribe((name) => {
      const selected = this.platformOptions.find((p: any) => p.value === name);
      this.projectForm.controls.listsIp.setValue(selected?.ips?.length ? [...selected.ips] : []);
    });
  }

  private toNumber(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  private patchInitialValues(): void {
    const src = this.project ?? {};
    const op = src?.operatorRegistration ?? {};
    const company = src?.company ?? {};
    const adress = src?.adress ?? {};
    const sponsor = src?.sponsor ?? {};
    const deposit = src?.depositConfiguration ?? {};
    const withdraw = src?.withDrawConfiguration ?? {};
    const permission = src?.permissonConfiguration ?? {};
    const listCnpj = src?.listCnpj ?? {};
    this.accountGuid = (src?.id ?? '').toString();
    this.licensingId = this.resolveLicensingId(src, op);
    this.licensingFallbackLabel = this.resolveLicensingLabel(src, op);
    const mtls = Array.isArray(src?.mtls)
      ? src.mtls.map((x: any) => (x ?? '').toString().toLowerCase())
      : [];
    this.projectForm.patchValue({
      id: (src?.id ?? '').toString(),


      operatorRegistration: {
        id: (op?.id ?? '').toString(),
        nameProject: (op?.nameProject ?? '').toString(),
        site: (op?.site ?? '').toString(),
        productId: (op?.productId ?? '').toString(),
        bankProvider: (op?.bankProvider ?? '').toString(),
        currentPlatform: (op?.currentPlatform ?? '').toString(),
        percentGGR: this.toNumber(op?.percentGGR),
      },

      company: {
        cnpj: (company?.cnpj ?? '').toString(),
        razaoSocial: (company?.razaoSocial ?? '').toString(),
        email: (company?.email ?? '').toString(),
        phone: (company?.phone ?? '').toString(),
      },

      adress: {
        logradouro: (adress?.logradouro ?? '').toString(),
        numero: (adress?.numero ?? '').toString(),
        complemento: (adress?.complemento ?? '').toString(),
        cep: (adress?.cep ?? '').toString(),
        bairro: (adress?.bairro ?? '').toString(),
        cidade: (adress?.cidade ?? '').toString(),
        estado: (adress?.estado ?? '').toString(),
      },

      sponsor: {
        cpf: (sponsor?.cpf ?? '').toString(),
        name: (sponsor?.name ?? '').toString(),
        email: (sponsor?.email ?? '').toString(),
        phone: (sponsor?.phone ?? '').toString(),
      },

      depositValueMinRequest: this.toNumber(deposit?.valueMinRequest),
      depositValueMaxRequest: this.toNumber(deposit?.valueMaxRequest),

      valorMinimoSaqueAutomatico: this.toNumber(withdraw?.valorMinimoSaqueAutomatico),
      amountExpired: this.toNumber(withdraw?.amountExpired),
      valueExpired: this.toNumber(withdraw?.valueExpired),
      analyzeWithdrawal: !!withdraw?.analyzeWithdrawal,
      valueMinRequest: this.toNumber(withdraw?.valueMinRequest),
      valueMaxRequest: this.toNumber(withdraw?.valueMaxRequest),

      permissonWithdraw: !!permission?.permissonWithdraw,
      permissonDeposit: !!permission?.permissonDeposit,
      permissonTef: !!permission?.permissonTef,
      permissonCrypto: !!permission?.permissonCrypto,
      permissionGameApi: !!permission?.permissionGameApi,

      featureFlagRefundPix: !!src?.featureFlagRefundPix,
      featureFlagCpfWithdraw: !!src?.featureFlagCpfWithdraw,
      featureFlagCustomerBalance: !!src?.featureFlagCustomerBalance,
      featureFlagNewParam: !!src?.featureFlagNewParam,

      userBlackList: !!src?.userBlackList,

      listCnpj: {
        flagCnpjCashIn: !!listCnpj?.flagCnpjCashIn,
        flagCnpjCashOut: !!listCnpj?.flagCnpjCashOut,
        cnpjCashIn: Array.isArray(listCnpj?.cnpjCashIn) ? listCnpj.cnpjCashIn : [],
        cnpjCashOut: Array.isArray(listCnpj?.cnpjCashOut) ? listCnpj.cnpjCashOut : [],
      },

      validateIp: !!src?.validateIp,
      listsIp: Array.isArray(src?.listsIp) ? src.listsIp : [],

      mtls: {
        deposit: mtls.includes('deposit'),
        withdraw: mtls.includes('withdraw'),
        tef: mtls.includes('tef'),
      },
    });
  }

  private onlyDigits(value: string | null | undefined): string {
    return (value ?? '').replace(/\D/g, '');
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  loadLicensings(): void {
    if (this.licensingsLoading) return;

    this.licensingsLoading = true;

    this.operatorsService
      .getLicensingRegistration(1000, 1)
      .pipe(finalize(() => (this.licensingsLoading = false)))
      .subscribe({
        next: (value: any) => {
          const list = (value?.items ?? value?.data ?? value?.result ?? value ?? []) as any[];
          const map: { [id: string]: string } = {};

          for (const item of list ?? []) {
            const id = (item?.id ?? '').toString().trim();
            const label = (
              item?.description ??
              item?.razaoSocial ??
              item?.cnpj ??
              item?.id ??
              ''
            ).toString().trim();

            if (id && label) {
              map[id] = label;
            }
          }

          this.licensingMap = map;
        },
        error: () => {
          this.licensingMap = {};
        },
      });
  }

  loadPlatforms(): void {
    if (this.platformsLoading) return;

    this.platformsLoading = true;

    this.operatorsService
      .getPlatform(100, 1)
      .pipe(finalize(() => (this.platformsLoading = false)))
      .subscribe({
        next: (value: any) => {
          const list = (value?.tickets ?? value?.items ?? value?.data ?? []) as any[];

          this.platformOptions = (list ?? [])
            .map((p: any) => ({
              label: (p?.name ?? '').toString(),
              value: (p?.name ?? '').toString(),
              ips: Array.isArray(p?.ips) ? p.ips : [],
            }))
            .filter((x: any) => !!x.value);
        },
        error: () => {
          this.platformOptions = [];
        },
      });
  }

  addCnpj(event: MatChipInputEvent, which: 'in' | 'out'): void {
    const value = (event.value || '').trim();
    event.chipInput?.clear();

    if (!value) return;

    const ctrl =
      which === 'in'
        ? this.projectForm.controls.listCnpj.controls.cnpjCashIn
        : this.projectForm.controls.listCnpj.controls.cnpjCashOut;

    const current = [...(ctrl.value ?? [])];

    if (current.includes(value)) {
      this.toastr.warning('CNPJ já adicionado.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    current.push(value);
    ctrl.setValue(current);
  }

  removeCnpj(value: string, which: 'in' | 'out'): void {
    const ctrl =
      which === 'in'
        ? this.projectForm.controls.listCnpj.controls.cnpjCashIn
        : this.projectForm.controls.listCnpj.controls.cnpjCashOut;

    const current = [...(ctrl.value ?? [])];
    ctrl.setValue(current.filter((x) => x !== value));
  }

  save(): void {
    this.projectForm.markAllAsTouched();

    if (this.projectForm.invalid) {
      this.expandInvalidSections();
      this.toastr.warning('Corrija os campos inválidos antes de salvar.', 'Aviso', {
        timeOut: 2500,
        positionClass: 'toast-top-right',
      });
      return;
    }

    this.openPasswordModal();
  }

  private anyInvalid(paths: string[]): boolean {
    return paths.some((p) => this.projectForm.get(p)?.invalid);
  }

  private expandInvalidSections(): void {
    this.isProjectExpanded = this.anyInvalid([
      'operatorRegistration.nameProject',
      'operatorRegistration.site',
      'operatorRegistration.productId',
      'operatorRegistration.bankProvider',
    ]);

    this.isCompanyExpanded = this.anyInvalid([
      'company.cnpj',
      'company.razaoSocial',
      'company.email',
      'company.phone',
    ]);

    this.isAddressExpanded = this.anyInvalid([
      'adress.logradouro',
      'adress.numero',
      'adress.complemento',
      'adress.cep',
      'adress.bairro',
      'adress.cidade',
      'adress.estado',
    ]);

    this.isSponsorExpanded = this.anyInvalid([
      'sponsor.cpf',
      'sponsor.name',
      'sponsor.email',
      'sponsor.phone',
    ]);

    this.isFinancialExpanded = this.anyInvalid([
      'depositValueMinRequest',
      'depositValueMaxRequest',
      'valorMinimoSaqueAutomatico',
      'amountExpired',
      'valueExpired',
      'valueMinRequest',
      'valueMaxRequest',
    ]);

    this.isPermissionsExpanded = this.anyInvalid([
      'permissonWithdraw',
      'permissonDeposit',
      'permissonTef',
      'permissonCrypto',
      'permissionGameApi',
      'featureFlagRefundPix',
      'featureFlagCpfWithdraw',
      'featureFlagCustomerBalance',
      'featureFlagNewParam',
      'userBlackList',
    ]);

    this.isCertificateExpanded = false;
    this.isIpExpanded = false;

    this.isListCnpjExpanded = this.anyInvalid([
      'listCnpj.flagCnpjCashIn',
      'listCnpj.flagCnpjCashOut',
      'listCnpj.cnpjCashIn',
      'listCnpj.cnpjCashOut',
    ]);

    if (
      !this.isProjectExpanded &&
      !this.isCompanyExpanded &&
      !this.isAddressExpanded &&
      !this.isSponsorExpanded &&
      !this.isFinancialExpanded &&
      !this.isPermissionsExpanded &&
      !this.isCertificateExpanded &&
      !this.isIpExpanded &&
      !this.isListCnpjExpanded
    ) {
      this.isProjectExpanded = true;
    }
  }

  openPasswordModal(): void {
    this.errorMessage = '';
    this.passwordInsert = '';

    this.passModalRef = this.modalService.open(this.requestPasswordInsert, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  confirmPasswordInsert(): void {
    if (this.validatingPassword || this.isSaving) return;

    const pass = (this.passwordInsert ?? '').toString().trim();
    if (!pass) {
      this.errorMessage = 'Informe a senha.';
      return;
    }

    this.validatingPassword = true;
    this.isSaving = true;

    const payload = this.buildPayload(pass);

    this.editProjectService
      .updateProject(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.validatingPassword = false;
        })
      )
      .subscribe({
        next: () => {
          this.passModalRef?.close();
          this.toastr.success('Projeto atualizado com sucesso!', 'Sucesso', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
          this.activeModal.close('saved');
        },
        error: () => {
          this.errorMessage = 'Erro ao validar senha ou atualizar projeto.';
        },
      });
  }

  private buildPayload(passwordInsert: string): any {
    const v = this.projectForm.getRawValue();

    const onlyDigits = (value: string | null | undefined): string =>
      (value ?? '').replace(/\D/g, '');

    const mtls: string[] = [
      ...(v.mtls?.deposit ? ['deposit'] : []),
      ...(v.mtls?.withdraw ? ['withdraw'] : []),
      ...(v.mtls?.tef ? ['tef'] : []),
    ];

    return {
      id: (v.id ?? '').toString(),
      operatorRegistration: {
        id: (v.operatorRegistration?.id ?? '').toString(),
        nameProject: (v.operatorRegistration?.nameProject ?? '').toString(),
        site: (v.operatorRegistration?.site ?? '').toString(),
        productId: (v.operatorRegistration?.productId ?? '').toString(),
        bankProvider: (v.operatorRegistration?.bankProvider ?? '').toString(),
        currentPlatform: (v.operatorRegistration?.currentPlatform ?? '').toString(),
        percentGGR: Number(v.operatorRegistration?.percentGGR ?? 0) || 0,
      },
      company: {
        cnpj: onlyDigits((v.company?.cnpj ?? '').toString()),
        razaoSocial: (v.company?.razaoSocial ?? '').toString(),
        email: (v.company?.email ?? '').toString(),
        phone: (v.company?.phone ?? '').toString(),
      },
      adress: {
        logradouro: (v.adress?.logradouro ?? '').toString(),
        numero: (v.adress?.numero ?? '').toString(),
        complemento: (v.adress?.complemento ?? '').toString(),
        cep: (v.adress?.cep ?? '').toString(),
        bairro: (v.adress?.bairro ?? '').toString(),
        cidade: (v.adress?.cidade ?? '').toString(),
        estado: (v.adress?.estado ?? '').toString(),
      },
      sponsor: {
        cpf: onlyDigits((v.sponsor?.cpf ?? '').toString()),
        name: (v.sponsor?.name ?? '').toString(),
        email: (v.sponsor?.email ?? '').toString(),
        phone: (v.sponsor?.phone ?? '').toString(),
      },
      depositValueMinRequest: Number(v.depositValueMinRequest ?? 0) || 0,
      depositValueMaxRequest: Number(v.depositValueMaxRequest ?? 0) || 0,
      valorMinimoSaqueAutomatico:
        Number(v.valorMinimoSaqueAutomatico ?? 0) || 0,
      amountExpired: Number(v.amountExpired ?? 0) || 0,
      valueExpired: Number(v.valueExpired ?? 0) || 0,
      analyzeWithdrawal: !!v.analyzeWithdrawal,
      valueMinRequest: Number(v.valueMinRequest ?? 0) || 0,
      valueMaxRequest: Number(v.valueMaxRequest ?? 0) || 0,
      permissonWithdraw: !!v.permissonWithdraw,
      permissonDeposit: !!v.permissonDeposit,
      permissonTef: !!v.permissonTef,
      permissonCrypto: !!v.permissonCrypto,
      permissionGameApi: !!v.permissionGameApi,
      featureFlagRefundPix: !!v.featureFlagRefundPix,
      featureFlagCpfWithdraw: !!v.featureFlagCpfWithdraw,
      featureFlagCustomerBalance: !!v.featureFlagCustomerBalance,
      featureFlagNewParam: !!v.featureFlagNewParam,
      userBlackList: !!v.userBlackList,

      mtls,
      listsIp: (v.listsIp ?? []).map((x: string) => (x ?? '').trim()).filter(Boolean),
      validateIp: !!v.validateIp,

      listCnpj: {
        flagCnpjCashIn: !!v.listCnpj?.flagCnpjCashIn,
        flagCnpjCashOut: !!v.listCnpj?.flagCnpjCashOut,
        cnpjCashIn: (v.listCnpj?.cnpjCashIn ?? []).map((x: string) =>
          onlyDigits(x)
        ),
        cnpjCashOut: (v.listCnpj?.cnpjCashOut ?? []).map((x: string) =>
          onlyDigits(x)
        ),
      },
      passwordInsert: passwordInsert,
    };
  }

  rangesValidator(control: AbstractControl): ValidationErrors | null {
    const getNum = (path: string): number => {
      const raw = control.get(path)?.value;
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };

    const errors: ValidationErrors = {};

    const withdrawMin = getNum('valueMinRequest');
    const withdrawMax = getNum('valueMaxRequest');
    if (withdrawMax < withdrawMin) errors['withdrawRangeInvalid'] = true;

    const depositMin = getNum('depositValueMinRequest');
    const depositMax = getNum('depositValueMaxRequest');
    if (depositMax < depositMin) errors['depositRangeInvalid'] = true;

    return Object.keys(errors).length ? errors : null;
  }

  hasRangeError(type: 'withdraw' | 'deposit'): boolean {
    const errors = this.projectForm.errors ?? {};
    return type === 'withdraw' ? !!errors['withdrawRangeInvalid'] : !!errors['depositRangeInvalid'];
  }

  async copyGuid(): Promise<void> {
    const value = (this.accountGuid ?? '').trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      this.toastr.success('GUID copiado com sucesso!', 'Sucesso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    } catch {
      this.toastr.error('Erro ao copiar GUID.', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    }
  }

  addIp(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    event.chipInput?.clear();

    if (!value) return;

    const ctrl = this.projectForm.controls.listsIp;
    const current = [...(ctrl.value ?? [])];

    if (current.includes(value)) {
      this.toastr.warning('IP já adicionado.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    current.push(value);
    ctrl.setValue(current);
  }

  removeIp(value: string): void {
    const ctrl = this.projectForm.controls.listsIp;
    const current = [...(ctrl.value ?? [])];
    ctrl.setValue(current.filter((x) => x !== value));
  }

  licensingLabel(licensingId: any): string {
    const id = this.normalizeLicensingId(licensingId);
    if (!id) return this.licensingFallbackLabel || '-';
    return this.licensingMap[id] || this.licensingFallbackLabel || '-';
  }

  private resolveLicensingId(src: any, op: any): string {
    return this.normalizeLicensingId(
      (
      op?.licensingRegistrationId ??
      src?.licensingRegistrationId ??
      op?.licensingRegistration?.id ??
      src?.licensingRegistration?.id ??
      ''
      ).toString()
    );
  }

  private resolveLicensingLabel(src: any, op: any): string {
    const licensing = op?.licensingRegistration ?? src?.licensingRegistration ?? {};

    return (
      licensing?.description ??
      licensing?.razaoSocial ??
      licensing?.cnpj ??
      '-'
    ).toString();
  }

  private normalizeLicensingId(value: any): string {
    const id = (value ?? '').toString().trim();
    if (!id || id === this.emptyGuid) {
      return '';
    }

    return id;
  }
}
